import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode as amplifyResendSignUpCode,
  resetPassword,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
} from 'aws-amplify/auth'

import { AUTH_EXPIRED_EVENT, getMe, type MeResponse } from '../lib/api'
import { authDisabled, configureAmplifyAuth, getCurrentAuthUser } from '../lib/authClient'
import { clearAttempts } from '../lib/beacon/attempts'
import { clearAllPendingJobs } from '../lib/pendingJob'
import { clearSessionCache } from '../lib/sessionCache'

/**
 * Adapted from aivy-coach `b0da0e2:frontend/src/contexts/AuthContext.tsx` (328
 * lines). See ../lib/authClient.ts for why that commit and not HEAD.
 *
 * KEPT, because each one is a production bug someone already hit:
 *   - existing-session adoption, so a second sign-in does not throw
 *     `UserAlreadyAuthenticatedException`;
 *   - orphaned-session recovery — tokens that resolve but no account behind
 *     them — which clears the session and retries once;
 *   - `CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED`, forwarding the
 *     `missingAttributes` Cognito asks for, without which the challenge fails
 *     with "name is missing";
 *   - "signed_in" meaning the account actually loaded, so the button never
 *     dead-ends silently;
 *   - the auth-expired event, renamed to `brainspot:auth-expired` (§3.2).
 *
 * STRIPPED: `acceptTerms`, `completeSetup`, `TERMS_REQUIRED_EVENT` and the
 * `aivy_session_id` localStorage key — all AIVY domain (§3.2).
 *
 * ADDED: `signUp` / `confirmSignUp` / `resendSignUpCode`. §3.3 calls this
 * "net-new work, not a port" and is **correct**: at `b0da0e2` AIVY is
 * invite-only and has no `Signup.tsx` and no self-registration in this file.
 * (Its HEAD has both, from SPRINT-SIGNUP-1 on 2026-08-26 — usable as a
 * reference for the call shapes, not as a port.)
 */

export type SignInResult = 'signed_in' | 'new_password_required'
export type SignUpResult = 'confirm_required' | 'signed_up'

interface AuthContextValue {
  account: MeResponse | null
  isLoading: boolean
  authError: string | null
  /** Attributes Cognito requires on the NEW_PASSWORD_REQUIRED challenge. */
  pendingMissingAttributes: string[]
  signIn: (email: string, password: string) => Promise<SignInResult>
  confirmNewPassword: (password: string, attributes?: Record<string, string>) => Promise<void>
  signUp: (email: string, password: string) => Promise<SignUpResult>
  confirmSignUp: (email: string, code: string) => Promise<void>
  resendSignUpCode: (email: string) => Promise<void>
  requestPasswordReset: (email: string) => Promise<void>
  confirmPasswordReset: (email: string, code: string, password: string) => Promise<void>
  refreshAccount: () => Promise<MeResponse | null>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const isAuthDisabled = authDisabled()
  const [account, setAccount] = useState<MeResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [authError, setAuthError] = useState<string | null>(null)
  const [pendingMissingAttributes, setPendingMissingAttributes] = useState<string[]>([])

  const refreshAccount = useCallback(async (): Promise<MeResponse | null> => {
    if (isAuthDisabled) {
      try {
        const me = await getMe()
        setAccount(me)
        setAuthError(null)
        return me
      } catch {
        setAccount(null)
        return null
      }
    }

    configureAmplifyAuth()
    try {
      await getCurrentAuthUser()
      const me = await getMe()
      setAccount(me)
      setAuthError(null)
      return me
    } catch {
      setAccount(null)
      return null
    }
  }, [isAuthDisabled])

  useEffect(() => {
    let isMounted = true

    const loadInitialAccount = async () => {
      try {
        if (!isAuthDisabled) await getCurrentAuthUser()
        const me = await getMe()
        if (!isMounted) return
        setAccount(me)
        setAuthError(null)
      } catch {
        if (!isMounted) return
        setAccount(null)
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    if (!isAuthDisabled) configureAmplifyAuth()
    void loadInitialAccount()
    return () => {
      isMounted = false
    }
  }, [isAuthDisabled])

  useEffect(() => {
    const handleAuthExpired = () => {
      if (!isAuthDisabled) void amplifySignOut().catch(() => undefined)
      setPendingMissingAttributes([])
      setAuthError('Your sign-in expired. Please sign in again.')
      setAccount(null)
      // §2.8: the resume pointer is per-user browser state and it goes when the
      // session does. An expiry is a sign-out that nobody clicked, so it has to
      // clear the same things -- otherwise the next person to use this browser
      // resumes a stranger's upload.
      clearAllPendingJobs()
      clearAttempts()
      clearSessionCache()
    }
    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired)
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired)
  }, [isAuthDisabled])

  const signIn = useCallback(
    async (email: string, password: string): Promise<SignInResult> => {
      setAuthError(null)
      if (isAuthDisabled) {
        const me = await refreshAccount()
        if (!me) throw new Error('The local development account is unavailable.')
        return 'signed_in'
      }

      const username = email.trim().toLowerCase()

      const startSignIn = async (): Promise<SignInResult> => {
        const result = await amplifySignIn({ username, password })
        if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          setPendingMissingAttributes(result.nextStep.missingAttributes ?? [])
          return 'new_password_required'
        }
        // "signed_in" has to mean the account actually loaded, or the
        // resolved-state redirect has nothing to route on and the screen goes
        // silent. Surface a real error instead of a false success.
        const me = await refreshAccount()
        if (!me) throw new Error('Signed in, but we could not load your account. Please try again.')
        return 'signed_in'
      }

      try {
        // If Amplify already holds a session, adopt it rather than calling
        // signIn again -- which throws UserAlreadyAuthenticatedException.
        try {
          await getCurrentAuthUser()
          const me = await refreshAccount()
          if (me) return 'signed_in'
          // Tokens with no resolvable account: an orphaned session. Clear it and
          // fall through to a real sign-in.
          await amplifySignOut()
        } catch {
          // No current session -- the ordinary first-time path.
        }

        try {
          return await startSignIn()
        } catch (err) {
          // A session appeared between the guard and signIn. Adopt, or reset and
          // retry exactly once.
          if (err instanceof Error && err.name === 'UserAlreadyAuthenticatedException') {
            const me = await refreshAccount()
            if (me) return 'signed_in'
            await amplifySignOut()
            return await startSignIn()
          }
          throw err
        }
      } catch (err) {
        // One funnel, so the button never dead-ends without saying why.
        setAuthError(err instanceof Error ? err.message : 'Sign-in failed.')
        throw err
      }
    },
    [isAuthDisabled, refreshAccount],
  )

  const confirmNewPassword = useCallback(
    async (password: string, attributes?: Record<string, string>) => {
      setAuthError(null)
      if (isAuthDisabled) return
      try {
        // Forward whatever Cognito flagged as missing on the challenge (`name`,
        // typically); without it the response fails with "name is missing".
        const hasAttributes = !!attributes && Object.keys(attributes).length > 0
        await confirmSignIn(
          hasAttributes
            ? { challengeResponse: password, options: { userAttributes: attributes } }
            : { challengeResponse: password },
        )
        setPendingMissingAttributes([])
        await refreshAccount()
      } catch (err) {
        setAuthError(err instanceof Error ? err.message : 'The password change failed.')
        throw err
      }
    },
    [isAuthDisabled, refreshAccount],
  )

  const signUp = useCallback(
    async (email: string, password: string): Promise<SignUpResult> => {
      setAuthError(null)
      if (isAuthDisabled) return 'confirm_required'
      const username = email.trim().toLowerCase()
      try {
        const result = await amplifySignUp({
          username,
          password,
          options: { userAttributes: { email: username } },
        })
        // The pool requires email verification, so the expected next step is a
        // code. `signUp` is handled anyway rather than assumed away: a pool
        // configured to auto-confirm would otherwise strand the user on a
        // verification screen with no code coming.
        return result.nextStep.signUpStep === 'DONE' ? 'signed_up' : 'confirm_required'
      } catch (err) {
        setAuthError(err instanceof Error ? err.message : 'Sign-up failed.')
        throw err
      }
    },
    [isAuthDisabled],
  )

  const confirmSignUp = useCallback(
    async (email: string, code: string) => {
      setAuthError(null)
      if (isAuthDisabled) return
      try {
        await amplifyConfirmSignUp({
          username: email.trim().toLowerCase(),
          confirmationCode: code.trim(),
        })
      } catch (err) {
        setAuthError(err instanceof Error ? err.message : 'That code was not accepted.')
        throw err
      }
    },
    [isAuthDisabled],
  )

  const resendSignUpCode = useCallback(
    async (email: string) => {
      setAuthError(null)
      if (isAuthDisabled) return
      await amplifyResendSignUpCode({ username: email.trim().toLowerCase() })
    },
    [isAuthDisabled],
  )

  const requestPasswordReset = useCallback(
    async (email: string) => {
      setAuthError(null)
      if (isAuthDisabled) return
      await resetPassword({ username: email.trim().toLowerCase() })
    },
    [isAuthDisabled],
  )

  const confirmPasswordReset = useCallback(
    async (email: string, code: string, password: string) => {
      setAuthError(null)
      if (isAuthDisabled) return
      await confirmResetPassword({
        username: email.trim().toLowerCase(),
        confirmationCode: code.trim(),
        newPassword: password,
      })
    },
    [isAuthDisabled],
  )

  const signOut = useCallback(async () => {
    if (!isAuthDisabled) await amplifySignOut()
    setPendingMissingAttributes([])
    setAuthError(null)
    setAccount(null)
    // Before the provider settles, and unconditionally. `clearAllPendingJobs`
    // drops EVERY user's pointer rather than the signed-in one's, because a
    // shared machine is how one person's upload ends up on another person's
    // screen and "the user we think is signed in" is the least reliable input
    // available at sign-out time. Beacon's check-in attempts
    // (`lib/beacon/attempts.ts`) are cleared the same way, for the same reason,
    // in the same order as the expiry handler above.
    clearAllPendingJobs()
    clearAttempts()
    // Home's in-memory answers (`lib/sessionCache.ts`) go with the session too.
    clearSessionCache()
    if (isAuthDisabled) await refreshAccount()
  }, [isAuthDisabled, refreshAccount])

  const value = useMemo<AuthContextValue>(
    () => ({
      account,
      isLoading,
      authError,
      pendingMissingAttributes,
      signIn,
      confirmNewPassword,
      signUp,
      confirmSignUp,
      resendSignUpCode,
      requestPasswordReset,
      confirmPasswordReset,
      refreshAccount,
      signOut,
    }),
    [
      account,
      isLoading,
      authError,
      pendingMissingAttributes,
      signIn,
      confirmNewPassword,
      signUp,
      confirmSignUp,
      resendSignUpCode,
      requestPasswordReset,
      confirmPasswordReset,
      refreshAccount,
      signOut,
    ],
  )

  return (
    <AuthContext value={value}>
      {isAuthDisabled && (
        <div className="dev-auth-banner" role="status">
          DEV — auth off
        </div>
      )}
      {children}
    </AuthContext>
  )
}
